import React from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarClock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/AuthContext'
import { useFormatters } from '@/lib/hooks/useFormatters'
import { useRoute } from '@/lib/RouteContext'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import { sdk } from '@/lib/sdk'

/**
 * F-199: the account's erasure is scheduled and the person still has paid
 * access, so the login goes on until the date (`/auth/me` brings
 * `erasureScheduledAt`). One line on every screen, with "Desistir" and the
 * way to the details (Minha Conta › Privacidade). Without paid access there is
 * no session to show it in: the login explains instead.
 */
export const ErasureBanner = () => {
  const { t } = useTranslation('privacy')
  const { activeMember, refreshMe } = useAuth()
  const { formatLongDate } = useFormatters()
  const { navigate } = useRoute()
  const [sending, setSending] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const scheduledAt = typeof activeMember?.erasureScheduledAt === 'string' ? activeMember.erasureScheduledAt : null
  if (!scheduledAt) return null

  const giveUp = async () => {
    setSending(true)
    setError(null)
    try {
      await sdk.privacy.cancelErasure()
      await refreshMe()
    } catch (err) {
      setError(apiErrorMessage(err).message || t('erasure.error'))
    } finally {
      setSending(false)
    }
  }

  return (
    <div role="status" className="border-b border-border bg-warning-surface px-4 py-3 text-sm text-warning-text sm:px-6" data-testid="erasure-banner">
      <div className="mx-auto flex max-w-7xl flex-col gap-2 sm:flex-row sm:items-center">
        <p className="flex min-w-0 flex-1 items-start gap-2">
          <CalendarClock className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{t('erasure.banner.text', { date: formatLongDate(scheduledAt) })}</span>
        </p>
        <div className="flex flex-shrink-0 gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/account?tab=privacy')}>{t('erasure.banner.details')}</Button>
          <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => void giveUp()} disabled={sending} data-testid="erasure-banner-give-up">
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('erasure.banner.giveUp')}
          </Button>
        </div>
      </div>
      {error && <p role="alert" className="mx-auto mt-2 max-w-7xl text-danger-text">{error}</p>}
    </div>
  )
}
